import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addMonths, subMonths, parseISO, differenceInCalendarDays, addDays, endOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Sparkles, Loader2, Droplet, CalendarCheck, AlertTriangle, Music, Flag, Star, Cake, Lock, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useCollection, usePrefs } from "@/hooks/useCollection";
import { fmtMonth, fmtDate, monthKey, iso } from "@/lib/format";
import { daySets, predictions, isOffDay, cycleStats, isPiketDay, isWeeklyPiket } from "@/lib/period";
import { MonthCalendar } from "@/components/mood/MonthCalendar";
import { DayDialog } from "@/components/mood/DayDialog";
import { Section } from "@/components/duitku/shared";
import { Button } from "@/components/ui/button";

const WEEKDAYS = [[1, "Sen"], [2, "Sel"], [3, "Rab"], [4, "Kam"], [5, "Jum"], [6, "Sab"], [0, "Min"]];
const CAT_ICON = { konser: Music, f1: Flag, fanmeet: Star, ultah: Cake };

const PredictionCard = ({ periods, prefs }) => {
  const next = predictions(periods, prefs, 1)[0];
  const { cycle, length } = cycleStats(periods, prefs);
  const days = next ? differenceInCalendarDays(parseISO(next.start), new Date()) : null;
  return (
    <Section className="bg-gradient-to-br from-rose-50 to-pink-50">
      <div className="flex items-center gap-2 text-rose-500 text-sm font-semibold"><Droplet className="w-4 h-4" /> Prediksi mens berikutnya</div>
      {next ? (
        <>
          <p className="font-heading text-3xl font-semibold text-gray-800 mt-2" data-testid="next-period-date">{fmtDate(next.start, "d MMMM")}</p>
          <p className="text-sm text-gray-500" data-testid="next-period-countdown">{days > 0 ? `${days} hari lagi` : "Sedang berlangsung / segera"} · siklus {cycle} hari · durasi {length} hari</p>
          <p className="text-xs text-gray-400 mt-1">Perkiraan ovulasi: {fmtDate(next.ovulation, "d MMM")}</p>
        </>
      ) : (
        <p className="text-sm text-gray-500 mt-2" data-testid="next-period-empty">Ketuk tanggal di kalender lalu pilih "Mens mulai hari ini" untuk mulai prediksi.</p>
      )}
    </Section>
  );
};

const Suggestions = ({ mk, sets, dayMap, prefs, events, holidays, left, onPick }) => {
  const today = iso(new Date());
  const pdays = [...sets.actual, ...sets.predicted].filter((d) => d.startsWith(mk)).sort();
  const firsts = pdays.filter((d) => !pdays.includes(iso(addDays(parseISO(d), -1))));
  const cands = [
    ...firsts.flatMap((d) => [[d, "hari pertama mens"], [iso(addDays(parseISO(d), 1)), "hari kedua mens"]]),
    ...Object.values(holidays).map((h) => [h.date, `libur nasional: ${h.name}`]),
    ...events.map((e) => [e.date, e.title]),
  ].filter(([d], i, arr) => d >= today && d.startsWith(mk) && !isOffDay(d, dayMap) && !isPiketDay(d, dayMap, prefs) && arr.findIndex(([x]) => x === d) === i);
  if (left <= 0 || cands.length === 0) return null;
  return (
    <div className="mt-3" data-testid="plan-suggestions">
      <p className="text-xs font-semibold text-pink-600 mb-2">Saran libur (ketuk untuk pakai):</p>
      <div className="flex flex-wrap gap-2">
        {cands.slice(0, left).map(([d, why]) => (
          <button key={d} onClick={() => onPick(d)} className="chip chip-off !text-xs max-w-full" data-testid={`plan-suggest-${d}`}><span className="truncate">{fmtDate(d, "d MMM")} · {why}</span></button>
        ))}
      </div>
    </div>
  );
};

const PlanCard = ({ month, sets, dayMap, prefs, events, holidays, onTakeOff }) => {
  const mk = monthKey(month);
  const quota = prefs?.off_quota ?? 3;
  const todayStr = iso(new Date());
  const periodWork = [...sets.actual, ...sets.predicted].filter((d) => d.startsWith(mk) && d >= todayStr && !isOffDay(d, dayMap) && !isPiketDay(d, dayMap, prefs)).sort();
  const monthDays = Array.from({ length: endOfMonth(month).getDate() }, (_, i) => iso(addDays(parseISO(`${mk}-01`), i)));
  const offCount = monthDays.filter((d) => isOffDay(d, dayMap)).length;
  const piketCount = monthDays.filter((d) => isPiketDay(d, dayMap, prefs)).length;
  const left = quota - offCount;
  return (
    <Section title={<span className="flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-sky-400" /> Rencana liburku</span>}>
      <p className="text-sm text-gray-500">Jatah libur <strong>{quota} hari</strong> bulan ini · terpakai <strong className="text-sky-600" data-testid="plan-off-count">{offCount} hari</strong> · sisa <strong className={left < 0 ? "text-rose-500" : "text-emerald-500"} data-testid="plan-off-left">{Math.max(left, 0)} hari</strong> · piket <strong className="text-amber-600" data-testid="plan-piket-count">{piketCount} hari</strong></p>
      {left < 0 && <p className="text-xs text-rose-500 mt-1" data-testid="plan-over-quota">Libur melebihi jatah {Math.abs(left)} hari.</p>}
      {periodWork.length > 0 && (
        <div className="mt-3 flex gap-2 text-sm bg-amber-50 text-amber-700 rounded-2xl p-3" data-testid="plan-period-clash">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Mens (atau prediksinya) jatuh di hari kerja: {periodWork.slice(0, 5).map((d) => fmtDate(d, "d MMM")).join(", ")}. Pertimbangkan ambil libur di hari pertama/kedua.</span>
        </div>
      )}
      <Suggestions mk={mk} sets={sets} dayMap={dayMap} prefs={prefs} events={events} holidays={holidays} left={left} onPick={onTakeOff} />
      {Object.values(holidays).some((h) => h.date.startsWith(mk)) && (
        <div className="mt-4 space-y-1.5" data-testid="plan-holiday-list">
          <p className="text-xs font-semibold text-red-500 flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5" /> Libur nasional & cuti bersama bulan ini</p>
          {Object.values(holidays).filter((h) => h.date.startsWith(mk)).map((h) => (
            <p key={h.date} className="text-sm text-gray-600" data-testid="plan-holiday-item"><strong className="text-red-500">{fmtDate(h.date, "EEE, d MMM")}</strong> · {h.name}{h.type === "cuti_bersama" ? " (cuti bersama)" : ""}</p>
          ))}
          <p className="text-[10px] text-gray-400">Dicari otomatis dari berita Google, cek lagi ya.</p>
        </div>
      )}
      <div className="mt-4 space-y-2" data-testid="plan-event-list">
        {events.length === 0 && <p className="text-sm text-gray-400">Belum ada acara bulan ini. Coba tombol "Cari acara seru" di atas.</p>}
        {[...events].sort((a, b) => a.date.localeCompare(b.date)).map((e) => {
          const I = CAT_ICON[e.category] || Sparkles;
          const clash = sets.actual.has(e.date) || sets.predicted.has(e.date);
          const off = isOffDay(e.date, dayMap);
          return (
            <div key={e.id} className="flex items-center gap-3 rounded-2xl border border-violet-100 bg-violet-50/50 px-3 py-2.5" data-testid="plan-event-item">
              <span className="w-9 h-9 rounded-xl bg-white text-violet-500 grid place-items-center shrink-0"><I className="w-4 h-4" /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700 truncate">{e.title}</p>
                <p className="text-xs text-gray-400 truncate">{fmtDate(e.date, "EEE, d MMM")}{e.place ? ` · ${e.place}` : ""}{e.source === "ai" ? " · saran otomatis, cek lagi ya" : ""}</p>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${clash ? "bg-rose-100 text-rose-600" : off ? "bg-sky-100 text-sky-600" : "bg-amber-100 text-amber-700"}`}>
                {clash ? "Bentrok mens" : off ? "Pas libur" : "Hari kerja"}
              </span>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

export default function MoodAku() {
  const [month, setMonth] = useState(new Date());
  const mk = monthKey(month);
  const periodsApi = useCollection("periods");
  const eventsApi = useCollection("events", mk);
  const { items: birthdays } = useCollection("birthdays");
  const allEvents = useMemo(() => [
    ...eventsApi.items,
    ...birthdays.filter((b) => b.date.slice(5, 7) === mk.slice(5)).map((b) => ({ id: `bd-${b.id}`, title: `Ultah ${b.name}`, date: `${mk.slice(0, 4)}${b.date.slice(4)}`, category: "ultah", source: "birthday" })),
  ], [eventsApi.items, birthdays, mk]);
  const { prefs, save: savePrefs } = usePrefs();
  const [dayMap, setDayMap] = useState({});
  const [picked, setPicked] = useState(null);
  const [finding, setFinding] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [holidays, setHolidays] = useState({});
  const [holBusy, setHolBusy] = useState(false);
  const triedYears = useRef(new Set());
  const year = month.getFullYear();

  const loadHolidays = useCallback(async (force) => {
    setHolBusy(true);
    try {
      let { data } = await api.get("/holidays", { params: { year } });
      if (force || (data.length === 0 && !triedYears.current.has(year))) {
        triedYears.current.add(year);
        data = (await api.post("/holidays/refresh", { year })).data;
      }
      setHolidays(Object.fromEntries(data.map((h) => [h.date, h])));
      if (force) toast.success(`Ketemu ${data.length} libur nasional ${year}`);
    } catch (e) {
      if (force) toast.error(errMsg(e));
    } finally {
      setHolBusy(false);
    }
  }, [year]);

  useEffect(() => {
    loadHolidays(false);
  }, [loadHolidays]);

  const loadDays = useCallback(async () => {
    const { data } = await api.get("/days", { params: { month: mk } });
    setDayMap(Object.fromEntries(data.map((d) => [d.date, d])));
  }, [mk]);

  useEffect(() => {
    loadDays();
  }, [loadDays]);

  const sets = useMemo(() => daySets(periodsApi.items, prefs), [periodsApi.items, prefs]);

  const saveDay = async (date, body) => {
    try {
      await api.put(`/days/${date}`, body);
      setDayMap((p) => ({ ...p, [date]: { ...p[date], ...body, date } }));
      toast.success("Tersimpan!");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const takeOff = (date) => saveDay(date, { mood: dayMap[date]?.mood || "", note: dayMap[date]?.note || "", status: "off" });

  const togglePickOff = (date) => {
    if (!date.startsWith(mk)) return;
    const base = { mood: dayMap[date]?.mood || "", note: dayMap[date]?.note || "" };
    if (isOffDay(date, dayMap)) return saveDay(date, { ...base, status: "default" });
    if (isPiketDay(date, dayMap, prefs)) return toast.error("Hari piket tidak bisa diambil libur");
    if (date < iso(new Date())) return toast.error("Tanggal yang sudah lewat tidak bisa dipilih");
    const used = Object.values(dayMap).filter((d) => d.date.startsWith(mk) && d.status === "off").length;
    if (used >= (prefs?.off_quota ?? 3)) return toast.error(`Jatah libur bulan ini (${prefs?.off_quota ?? 3} hari) sudah habis`);
    return takeOff(date);
  };

  const togglePiket = (n) => {
    const cur = prefs.piket_weekly || [];
    savePrefs({ piket_weekly: cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n] });
  };

  const findEvents = async () => {
    setFinding(true);
    try {
      const { data } = await api.post("/events/refresh", { month: mk });
      await eventsApi.reload();
      toast.success(data.length ? `Ketemu ${data.length} acara seru!` : "Belum ketemu acara bulan ini");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFinding(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-pink-500 font-medium">Siklusku, jadwal kerjaku & acara seru</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-gray-800">Mood Aku</h1>
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        <Section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4 gap-2">
            <button onClick={() => setMonth(subMonths(month, 1))} className="p-2 rounded-full hover:bg-pink-50 text-pink-500" data-testid="calendar-prev-month" aria-label="Bulan sebelumnya"><ChevronLeft className="w-5 h-5" /></button>
            <h2 className="font-heading text-xl font-semibold text-gray-800 capitalize" data-testid="calendar-month-label">{fmtMonth(month)}</h2>
            <button onClick={() => setMonth(addMonths(month, 1))} className="p-2 rounded-full hover:bg-pink-50 text-pink-500" data-testid="calendar-next-month" aria-label="Bulan berikutnya"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => setPickMode((p) => !p)} className={`chip ${pickMode ? "!bg-sky-400 !border-sky-400 text-white" : "chip-off"}`} data-testid="pick-off-mode-btn">
              <Lock className="w-4 h-4" /> {pickMode ? "Selesai pilih libur" : "Pilih hari libur"}
            </button>
            <button onClick={() => loadHolidays(true)} disabled={holBusy} className="chip chip-off" data-testid="refresh-holidays-btn">
              {holBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4 text-red-400" />} Perbarui libur nasional
            </button>
          </div>
          {pickMode && <p className="text-xs text-sky-600 bg-sky-50 rounded-2xl px-3 py-2 mb-3" data-testid="pick-off-hint">Ketuk tanggal untuk mengunci/membuka hari libur. Hari piket tidak bisa dipilih. Jatah {prefs?.off_quota ?? 3} hari/bulan.</p>}
          <MonthCalendar month={month} sets={sets} dayMap={dayMap} prefs={prefs} events={allEvents} holidays={holidays} pickMode={pickMode} onPick={pickMode ? togglePickOff : setPicked} />
          <div className="mt-4 pt-3 border-t border-pink-50">
            <p className="text-xs font-semibold text-amber-600 mb-2">Piket rutinku tiap minggu (tidak bisa libur)</p>
            <div className="flex gap-1.5 flex-wrap">
              {WEEKDAYS.map(([n, l]) => (
                <button key={n} disabled={!prefs} onClick={() => togglePiket(n)} className={`chip !px-3 ${prefs?.piket_weekly?.includes(n) ? "!bg-amber-400 !border-amber-400 text-white" : "chip-off"}`} data-testid={`piket-weekly-${n}`}>{l}</button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-2">Selain piket, semua hari dihitung kerja. Jadwal piket juga bisa diubah di Akunku.</p>
          </div>
        </Section>
        <div className="lg:col-span-2 space-y-4">
          <PredictionCard periods={periodsApi.items} prefs={prefs} />
          <Button onClick={findEvents} disabled={finding} className="w-full rounded-full h-11" variant="secondary" data-testid="find-events-btn">
            {finding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {finding ? "Lagi cari acara terbaru..." : "Cari acara seru bulan ini (konser, F1, dll)"}
          </Button>
          <PlanCard month={month} sets={sets} dayMap={dayMap} prefs={prefs} events={allEvents} holidays={holidays} onTakeOff={togglePickOff} />
        </div>
      </div>
      <DayDialog
        date={picked}
        onClose={() => setPicked(null)}
        day={picked ? dayMap[picked] : null}
        periods={periodsApi.items}
        events={allEvents.filter((e) => e.date === picked)}
        onSaveDay={saveDay}
        periodsApi={periodsApi}
        eventsApi={eventsApi}
        weeklyPiket={picked ? isWeeklyPiket(picked, prefs) : false}
      />
    </div>
  );
}
